import { useEffect, useMemo, useState } from "react";
import { FiUser, FiUserPlus, FiAlertCircle, FiUserCheck, FiClock } from "react-icons/fi";
import api from "../../services/api";

const NetworkSummary = () => {
  const [statusCounts, setStatusCounts] = useState({});
  const [levelCounts, setLevelCounts] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          return;
        }

        const response = await api.get("/dashboard/network-summary", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          return;
        }

        const data = response.data?.data || {};
        setStatusCounts(data.status_counts || {});
        setLevelCounts(data.level_counts || {});
      } catch (error) {
        return;
      }
    };

    fetchSummary();
  }, []);

  const getStatusCount = (target) => {
    const entries = Object.entries(statusCounts || {});
    const match = entries.find(
      ([name]) => String(name || "").toLowerCase() === target.toLowerCase()
    );
    return match ? match[1] : 0;
  };

  const levelBreakdown = useMemo(
    () =>
      [1, 2, 3, 4, 5].map(
        (level) => `L ${level} - ${levelCounts?.[level] || 0}`
      ),
    [levelCounts]
  );

  const activeCount =
    getStatusCount("For Activation") +
    getStatusCount("Active") +
    getStatusCount("Consumed");

  const statusCards = [
    {
      title: "Pre Registered",
      value: getStatusCount("Pre Registered"),
      icon: FiUserPlus,
      cardClass: "border-slate-200 bg-slate-50",
      badgeClass: "bg-slate-500",
      textClass: "text-slate-700",
    },
    {
      title: "For Validation",
      value: getStatusCount("For Validation"),
      icon: FiAlertCircle,
      cardClass: "border-amber-200 bg-amber-50",
      badgeClass: "bg-amber-500",
      textClass: "text-amber-700",
    },
    {
      title: "Active",
      value: activeCount,
      icon: FiUserCheck,
      cardClass: "border-emerald-200 bg-emerald-50",
      badgeClass: "bg-emerald-500",
      textClass: "text-emerald-700",
    },
    {
      title: "Expired",
      value: getStatusCount("Expired"),
      icon: FiClock,
      cardClass: "border-rose-200 bg-rose-50",
      badgeClass: "bg-rose-500",
      textClass: "text-rose-700",
    },
  ];

  return (
    <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
            Dashboard
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-slate-900">
            Network Summary
          </h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
          Live Overview
        </span>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-4">
        {statusCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className={`summary-card rounded-2xl border px-5 py-4 ${card.cardClass}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`summary-badge flex h-9 w-9 items-center justify-center rounded-full text-white ${card.badgeClass}`}
                >
                  <Icon />
                </span>
                <p className={`summary-title text-sm font-semibold ${card.textClass}`}>
                  {card.title}
                </p>
              </div>
              <p className="summary-value mt-4 text-4xl font-semibold text-slate-900">
                {card.value}
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Network Levels
        </div>
        <div className="mt-2 grid gap-2 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-5">
          {levelBreakdown.map((level) => (
            <div
              key={level}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center font-semibold text-slate-700"
            >
              {level}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NetworkSummary;
